<template>
  <b-container>
    <b-row class="justify-content-center d-flex align-items-center" style="height: 80vh;">
      <b-col md="5">
        <b-card title="Neues Passwort vergeben" sub-title="Bitte Starkes Passwort wählen">
          <b-alert v-model="hasApiErrors" variant="danger" dismissible>
            <div v-for="(error, index) in apiErrors" :key="index">
              {{ error }}
            </div>
          </b-alert>
          <b-alert v-model="userCreated" variant="success" dismissible>
            Dein Benutzerkonto wurde erfolgreich angelegt. Wir haben dir eine E-Mail an die Addresse <b>{{
            createdUser.email
          }}</b> gesendet.<br>
            Aktiviere jetzt deinen Account über den darin enthalten Link.
          </b-alert>
          <hr>
          <ValidationObserver v-slot="vo" ref="vObserver">
            <b-form @submit.prevent="onSubmit(vo)" ref="registerForm">
              <!-- Email/Username -->
              <b-form-group>
                <label label-for="email-input" class="mt-3">E-Mail (GFU Mailadresse) <span
                    class="text-danger">*</span></label>
                <ValidationProvider name="E-Mail" rules="required|gfuEmail" v-slot="v" ref="email" :events="'change'"
                  :mode="'lazy'">
                  <b-form-input :state="v.passed ? true : (v.failed) ? false : null" id="email-input" v-model="email"
                    type="text" placeholder="E-Mail eingeben" :disabled="emailDisabled" />
                  <b-form-invalid-feedback :state="v.passed">
                    {{ v.errors[0] }}
                  </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>

              <!-- Password -->
              <b-form-group>
                <label label-for="password-input">Passwort <span class="text-danger">*</span></label>
                <ValidationProvider name="Passwort" :rules="`required|minLength|lowercase|uppercase|number`" v-slot="v"
                  ref="password" :bails="false"><!-- |passwordMatch:${repeatedPassword} -->
                  <b-popover ref="passwordPopover" id="password-popover" target="password-input" placement="right"
                    triggers="focus">
                    <template #title>Passwort Regeln</template>
                    <div v-for="(validation, index) in passwordValidation" :key="index">
                      <div class="text-danger password-feedback" v-if="validation.status === 'failed'">
                        <b-icon icon="exclamation-circle-fill" scale="1.1"></b-icon>
                        <span class="ml-2" v-html="shortMessages[validation.name]"></span>
                      </div>
                      <div class="text-success password-feedback" v-if="validation.status === 'passed'">
                        <b-icon icon="check-circle-fill" scale="1.1"></b-icon>
                        <span class="ml-2" v-html="shortMessages[validation.name]"></span>
                      </div>
                    </div>
                  </b-popover>

                  <b-form-input :state="v.passed ? true : (lazyError !== null) ? false : null" id="password-input"
                    v-model="password" type="password" placeholder="Passwort eingeben" @focus.once="initValidation"
                    @change="lazyError = v.errors[0] ? v.errors[0] : null"
                    @blur="lazyError = v.errors[0] ? v.errors[0] : null" @keyup="() => onPasswordKeyup(v)"
                    ref="passwordInput" />
                  <b-form-invalid-feedback :state="lazyError === null" v-html="lazyError"></b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>

              <!-- Repeat Password -->
              <b-form-group>
                <label label-for="password-repeat-input">Passwort wiederholen <span class="text-danger">*</span></label>
                <ValidationProvider name="Passwort wiederholen" rules="required|confirmed:Passwort" v-slot="v"
                  :events="'change'">
                  <b-form-input :state="v.passed ? true : (v.failed) ? false : null" id="password-repeat-input"
                    v-model="passwordRepeat" type="password" placeholder="Passwort wiederholen"
                    ref="passwordRepeat" /><!-- @keyup="() => revalidatePassword(v)" @change="() => revalidatePassword(v)"-->
                  <b-form-invalid-feedback :state="false">
                    {{ v.errors[0] }}
                  </b-form-invalid-feedback>
                </ValidationProvider>
              </b-form-group>
              <!-- Submit Button -->
              <b-overlay :show="busy" rounded opacity="0.6" spinner-small spinner-variant="primary"
                class="d-inline-block">
                <b-button type="submit" variant="primary" :disabled="busy">
                  Jetzt registrieren
                </b-button>
              </b-overlay>
            </b-form>
          </ValidationObserver>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { required, confirmed } from 'vee-validate/dist/rules';
import Auth from '../misc/Auth.js';

// Add the existing rules to VeeValidate


extend('confirmed', {
  ...confirmed,
  message: 'Passwort wurde nicht korrekt wiederholt'
});

extend('gfuEmail', {
  validate: value => /@gfu\.net$/.test(value),
  message: '{_field_} muss eine GFU-Mail Addresse sein.',
});

extend('required', {
  ...required,
  message: '{_field_} darf nicht leer sein.'
});

// Custom rule for at least one lowercase letter
extend('minLength', {
  validate: value => value.length > 7,
  message(field) {
    return `${field} muss mindestens <b>8 Zeichen</b> lang sein.`
  }
  //message: `{_field_} muss aus <b>mindestens 8 Zeichen</b> bestehen.`
});

// Custom rule for at least one lowercase letter
extend('lowercase', {
  validate: value => /[a-z]/.test(value),
  message: '{_field_} muss mindestens einen <b>Kleinbuchstaben</b> enthalten.'
});

// Custom rule for at least one uppercase letter
extend('uppercase', {
  validate: value => /[A-Z]/.test(value),
  message: '{_field_} muss mindestens einen <b>Großbuchstaben</b> enthalten.'
});

// Custom rule for at least one number
extend('number', {
  validate: value => /\d/.test(value),
  message: '{_field_} muss mindestens eine <b>Zahl</b> enthalten.'
});

// Custom rule to ensure password matches the passwordRepeat field
extend('passwordMatch', {
  params: ['target'],
  validate(value, { target }) {
    return (value.length > 0) && (value === target);
  },
  message: '{_field_} muss mit korrekt wiederholt werden.'
});



export default {
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      email: '',
      password: '',
      passwordRepeat: '',
      passwordValidationRules: [],
      createdUser: {},
      lazyError: null,
      shortMessages: {
        'minLength': `Muss mind. <b>8 Zeichen</b> lang sein`,
        'lowercase': `Muss <b>Kleinbuchstaben</b> enthalten`,
        'uppercase': `Muss <b>Großbuchstaben</b> enthalen`,
        'number': `Muss <b>Zahlen</b> enthalten`
      },
      apiErrors: [],
      emailDisabled: false,
      busy: false,
    };
  },
  mounted() {
    this.initValidation();
    // this.$refs.passwordPopover.show();
    const email = this.$route.params.email
    if (email) {
      this.email = email;
      setTimeout(() =>
        this.$refs.email.validate()
          .then((result) => {
            console.log(result);
            if (result.valid) {
              this.emailDisabled = true;
              // this.$refs.passwordInput.focus();
            } else {
              this.apiErrors = ['Die übergebene E-Mail ist ungültig.']
            }
          })
        , 20)
    }
  },
  methods: {
    onSubmit(vo) {
      vo.handleSubmit(this.registerUser).then(result => {
        this.$refs.password.validate()
          .then(({ valid, errors }) => this.lazyError = (valid) ? null : errors[0])
      });
    },
    registerUser() {
      this.busy = true;
      Auth.register(this.email, this.password)
        .then(this.onRegisterSuccess)
        .catch(this.catchApiErrors);
    },
    onRegisterSuccess(res) {
      this.busy = false;
      this.apiErrors = [];
      this.createdUser = res.data;
      this.clearForm()
    },
    catchApiErrors(error) {
      this.createdUser = {};
      this.busy = false;
      if (error.response && error.response.status) {
        if (Array.isArray(error.response.data)) {
          this.apiErrors = error.response.data.map(({ message }) => message);
        } else {
          this.apiErrors = error.reponse.data.message;
        }
      } else {
        this.apiErrors = [error.message];
      }
      console.error(error);
    },
    clearForm() {
      this.email = '';
      this.password = '';
      this.passwordRepeat = '';
      this.lazyError = null;
      this.$refs.vObserver.reset();
    },
    onPasswordKeyup(v) {
      this.passwordValidation = v.failedRules;
      this.lazyError = v.errors[0]
    },
    initValidation() {
      this.$refs.password.validate()
        .then(result => {
          this.passwordValidationRules = Object.keys(result.failedRules).map(k => ({
            'message': result.failedRules[k],
            'name': k,
            'status': 'failed',
          }))
            .filter(({ name }) => name !== 'required');
        });
    },
    revalidatePassword(vRepeat) {
      vRepeat.validate()
        .then(({ valid }) => {
          if (valid) this.lazyError = null
        })
      this.$refs.password.validate()
        .then(v => this.passwordValidation = v.failedRules);
    },
    scrollToError(vo) {
      const errorField = Object.keys(vo.fields).map(key => vo.fields[key])
        .find(field => field.invalid);
      this.$refs.registerForm.refs[errorField.name].$el.scrollIntoView({ behavior: "smooth" })
    },
  },
  computed: {
    passwordValidation: {
      set(rules) {
        this.passwordValidationRules = this.passwordValidationRules.map((validation) => ({
          ...validation,
          'status': rules[validation.name] ? 'failed' : 'passed'
        }))
      },
      get() {
        return this.passwordValidationRules;
      }
    },
    hasApiErrors: {
      get() {
        return this.apiErrors.length > 0;
      },
      set() {
        this.apiErrors = [];
      }
    },
    userCreated: {
      get() {
        return !!this.createdUser.id;
      },
      set() {
        this.createdUser = {};
      }
    }
    // repeatedPassword() {
    //   return this.passwordRepeat;
    // },

  }
};
</script>

<style>
/* .password-feedback {
    font-weight: 500;
  } */

#password-popover h3 {
  margin-top: 0px
}
</style>